import { useState } from "react";
import { Modal } from "../../Modal";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { adminFetch } from "../../../lib/adminApi";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Check, X } from "lucide-react";

interface Tag {
  id: string;
  name_en: string;
  name_tr: string;
}

interface TagManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  tags: Tag[];
  onRefresh: () => void;
}

function TagForm({
  id, setId, nameEn, setNameEn, nameTr, setNameTr,
  editingTag, cancelEdit, onSubmit
}: any) {
  return (
    <form onSubmit={onSubmit} className="space-y-4 p-1 border-b border-border/60 pb-5">
      <h4 className="text-xs font-bold text-brand-primary uppercase tracking-wider">
        {editingTag ? "Edit Tag" : "Create New Tag"}
      </h4>
      <div className="grid grid-cols-3 gap-3">
        {!editingTag && (
          <div className="space-y-1">
            <Label htmlFor="tag-id">ID / Code</Label>
            <Input id="tag-id" placeholder="ai" value={id} onChange={(e) => setId(e.target.value)} required />
          </div>
        )}
        <div className={editingTag ? "col-span-2 space-y-1" : "space-y-1"}>
          <Label htmlFor="tag-name-en">English Name</Label>
          <Input id="tag-name-en" placeholder="Artificial Intelligence" value={nameEn} onChange={(e) => setNameEn(e.target.value)} required />
        </div>
        <div className="space-y-1">
          <Label htmlFor="tag-name-tr">Turkish Name</Label>
          <Input id="tag-name-tr" placeholder="Yapay Zeka" value={nameTr} onChange={(e) => setNameTr(e.target.value)} required />
        </div>
      </div>
      <div className="flex justify-end gap-2 text-xs">
        {editingTag && (
          <Button type="button" variant="outline" size="sm" onClick={cancelEdit}>
            <X className="h-3.5 w-3.5 mr-1" /> Cancel
          </Button>
        )}
        <Button type="submit" size="sm" className="bg-brand-primary text-white">
          {editingTag ? <><Check className="h-3.5 w-3.5 mr-1" /> Update</> : <><Plus className="h-3.5 w-3.5 mr-1" /> Add Tag</>}
        </Button>
      </div>
    </form>
  );
}

function TagList({
  tags,
  onEdit,
  onDelete,
}: {
  tags: Tag[];
  onEdit: (tag: Tag) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="space-y-2 pt-3 max-h-56 overflow-y-auto">
      <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Existing Tags</h4>
      {tags.length === 0 ? (
        <p className="text-xs italic text-muted-foreground py-4 text-center">No tags defined yet.</p>
      ) : (
        tags.map((tag) => (
          <div key={tag.id} className="flex items-center justify-between p-2.5 border border-border/40 rounded-xl bg-muted/10 text-xs">
            <div>
              <span className="font-bold text-brand-primary font-mono select-all">#{tag.id}</span>
              <span className="ml-2 font-medium text-foreground">{tag.name_en}</span>
              <span className="text-muted-foreground mx-1.5">|</span>
              <span className="font-medium text-foreground">{tag.name_tr}</span>
            </div>
            <div className="flex gap-1">
              <Button type="button" variant="ghost" size="icon" onClick={() => onEdit(tag)} className="h-7 w-7 text-brand-secondary">
                <Edit2 className="h-3.5 w-3.5" />
              </Button>
              <Button type="button" variant="ghost" size="icon" onClick={() => onDelete(tag.id)} className="h-7 w-7 text-destructive">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export function TagManagerModal({
  isOpen,
  onClose,
  tags,
  onRefresh,
}: TagManagerModalProps) {
  const [id, setId] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [nameTr, setNameTr] = useState("");
  const [editingTag, setEditingTag] = useState<Tag | null>(null);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const tagId = editingTag ? editingTag.id : id.trim().toLowerCase().replace(/[^a-z0-9-_]/g, "-");
    const en = nameEn.trim();
    const tr = nameTr.trim();

    if (!tagId || !en || !tr) {
      toast.error("Tag ID and both translation names are required.");
      return;
    }

    try {
      await adminFetch("/api/admin/blog-tags", { id: tagId, name_en: en, name_tr: tr });
      toast.success(editingTag ? "Tag updated successfully!" : "Tag added successfully!");
      setId("");
      setNameEn("");
      setNameTr("");
      setEditingTag(null);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to save tag");
    }
  };

  const handleDelete = async (tagId: string) => {
    if (!confirm("Are you sure you want to delete this tag? Any blogs with this tag will lose the association.")) return;
    try {
      await adminFetch("/api/admin/blog-tags", { operation: "delete", id: tagId });
      toast.success("Tag deleted successfully!");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete tag");
    }
  };

  const startEdit = (tag: Tag) => {
    setEditingTag(tag);
    setNameEn(tag.name_en);
    setNameTr(tag.name_tr);
  };

  const cancelEdit = () => {
    setEditingTag(null);
    setNameEn("");
    setNameTr("");
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Manage Blog Tags" subtitle="Configure tags used in blog articles">
      <TagForm
        id={id} setId={setId} nameEn={nameEn} setNameEn={setNameEn} nameTr={nameTr} setNameTr={setNameTr}
        editingTag={editingTag} cancelEdit={cancelEdit} onSubmit={handleSave}
      />
      <TagList tags={tags} onEdit={startEdit} onDelete={handleDelete} />
    </Modal>
  );
}
export default TagManagerModal;
